import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Check, Scale, ShoppingCart } from 'lucide-react';
import axios from 'axios';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function CompareButton({ product, isSelected, onToggle }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle(product);
      }}
      className={`p-2 rounded-full transition-all duration-200 shadow-lg ${
        isSelected
          ? 'bg-teal-500 text-black ring-2 ring-teal-400/50'
          : 'bg-slate-900/90 text-teal-400 border border-teal-500/40 hover:bg-teal-500 hover:text-black'
      }`}
      title={isSelected ? 'Remove from compare' : 'Add to compare'}
      data-testid={`compare-toggle-${product.id}`}
    >
      <Scale className="w-4 h-4" />
    </button>
  );
}

export function CompareBar({ selectedProducts, onRemove, onCompare, onClear }) {
  const { t } = useTranslation();

  if (selectedProducts.length === 0) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 p-4"
      data-testid="compare-bar"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Scale className="w-5 h-5 text-teal-400" />
            <span className="text-white font-medium">
              {selectedProducts.length} {selectedProducts.length === 1 ? t('compare.planSelected') : t('compare.plansSelected')}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {selectedProducts.map(product => (
              <div
                key={product.id}
                className="flex items-center space-x-2 bg-white/10 rounded-full pl-3 pr-1 py-1"
              >
                <span className="text-sm text-slate-300 truncate max-w-[100px]">
                  {product.name}
                </span>
                <button
                  onClick={() => onRemove(product.id)}
                  className="p-1 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={onClear}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Clear all
          </button>
          <button
            onClick={onCompare}
            disabled={selectedProducts.length < 2}
            className="bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-black font-semibold px-6 py-2 rounded-full transition-all duration-200"
            data-testid="compare-now-button"
          >
            {t('compare.compareNow')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function ComparePage({ selectedProducts, onBack, onAddToCart, onAddMore }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState(selectedProducts);

  const getMetaValue = (product, key) => {
    const meta = product.meta_data?.find(m => m.key === key);
    return meta?.value || '-';
  };

  const getDataGB = (product) => {
    const data = getMetaValue(product, 'data');
    const match = data.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const getValidityDays = (product) => {
    const validity = getMetaValue(product, 'validity');
    const match = validity.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const getPricePerGB = (product) => {
    const price = parseFloat(product.price);
    const gb = getDataGB(product);
    return gb > 0 ? (price / gb).toFixed(2) : '-';
  };

  const getPricePerDay = (product) => {
    const price = parseFloat(product.price);
    const days = getValidityDays(product);
    return days > 0 ? (price / days).toFixed(2) : '-';
  };

  const getBestValue = (accessor) => {
    if (products.length < 2) return null;
    let bestIdx = 0;
    let bestVal = accessor(products[0]);
    
    products.forEach((p, idx) => {
      const val = accessor(p);
      if (typeof val === 'number' && val < bestVal) {
        bestVal = val;
        bestIdx = idx;
      }
    });
    
    return products[bestIdx]?.id;
  };

  const bestPricePerGB = getBestValue((p) => parseFloat(getPricePerGB(p)) || Infinity);
  const bestPricePerDay = getBestValue((p) => parseFloat(getPricePerDay(p)) || Infinity);
  const lowestPrice = getBestValue((p) => parseFloat(p.price));
  const mostData = products.reduce((best, p) => getDataGB(p) > getDataGB(best) ? p : best, products[0])?.id;

  const comparisonRows = [
    { label: t('compare.price'), getValue: (p) => formatPrice(p.price), highlight: lowestPrice, highlightLabel: t('compare.lowest') },
    { label: t('compare.data'), getValue: (p) => getMetaValue(p, 'data'), highlight: mostData, highlightLabel: t('compare.mostData') },
    { label: t('compare.validity'), getValue: (p) => getMetaValue(p, 'validity'), highlight: null },
    { label: t('compare.pricePerGB'), getValue: (p) => formatPrice(getPricePerGB(p)), highlight: bestPricePerGB, highlightLabel: t('compare.bestValue') },
    { label: t('compare.pricePerDay'), getValue: (p) => formatPrice(getPricePerDay(p)), highlight: bestPricePerDay, highlightLabel: t('compare.bestValue') },
    { label: t('compare.coverage'), getValue: (p) => getMetaValue(p, 'country'), highlight: null },
    { label: t('compare.network'), getValue: () => '4G/5G', highlight: null }
  ];

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <button
                onClick={onBack}
                className="text-slate-400 hover:text-white mb-4 flex items-center space-x-2 transition-colors"
                data-testid="compare-back-button"
              >
                <span>{t('compare.backToShop')}</span>
              </button>
              <h1 className="text-3xl font-bold text-white font-outfit flex items-center space-x-3">
                <Scale className="w-8 h-8 text-teal-400" />
                <span>{t('compare.title')}</span>
              </h1>
            </div>

            {products.length < 4 && (
              <button
                onClick={onAddMore}
                className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full transition-colors"
                data-testid="add-more-button"
              >
                <Plus className="w-5 h-5" />
                <span>{t('compare.addMore')}</span>
              </button>
            )}
          </div>

          {products.length < 2 ? (
            <div className="text-center py-16">
              <Scale className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">{t('compare.selectAtLeast')}</h2>
              <p className="text-slate-400 mb-6">{t('compare.selectAtLeastDesc')}</p>
              <button
                onClick={onAddMore}
                className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-3 rounded-full transition-all"
              >
                {t('compare.browseMore')}
              </button>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
              {/* Header with product cards */}
              <div className="grid grid-cols-1 md:grid-cols-[200px_repeat(auto-fit,minmax(200px,1fr))] border-b border-white/10">
                <div className="p-4 bg-slate-900/50 hidden md:block" />
                
                {products.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 text-center border-l border-white/10 first:border-l-0"
                  >
                    <div className="relative">
                      <button
                        onClick={() => setProducts(products.filter(p => p.id !== product.id))}
                        className="absolute -top-2 -right-2 p-1 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                      
                      <img
                        src={product.images?.[0]?.src || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=200'}
                        alt={product.name}
                        className="w-20 h-20 rounded-xl object-cover mx-auto mb-4"
                      />
                      
                      <h3 className="text-lg font-semibold text-white mb-1">{product.name}</h3>
                      <p className="text-2xl font-bold text-teal-400 font-mono mb-4">€{parseFloat(product.price).toFixed(2)}</p>
                      
                      <button
                        onClick={() => onAddToCart(product)}
                        className="w-full bg-teal-500 hover:bg-teal-400 text-black font-semibold py-2 rounded-full transition-all duration-200 flex items-center justify-center space-x-2"
                        data-testid={`compare-add-to-cart-${product.id}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>{t('products.addToCart')}</span>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Comparison rows */}
              {comparisonRows.map((row, rowIdx) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-1 md:grid-cols-[200px_repeat(auto-fit,minmax(200px,1fr))] ${
                    rowIdx % 2 === 0 ? 'bg-white/[0.02]' : ''
                  }`}
                >
                  <div className="p-4 text-slate-400 font-medium border-r border-white/10 flex items-center">
                    {row.label}
                  </div>
                  
                  {products.map((product) => {
                    const isHighlighted = row.highlight === product.id;
                    
                    return (
                      <div
                        key={product.id}
                        className={`p-4 text-center border-l border-white/10 first:border-l-0 ${
                          isHighlighted ? 'bg-teal-500/10' : ''
                        }`}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <span className={`font-mono ${isHighlighted ? 'text-teal-400 font-bold' : 'text-white'}`}>
                            {row.getValue(product)}
                          </span>
                          {isHighlighted && (
                            <span className="flex items-center space-x-1 text-xs bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded-full">
                              <Check className="w-3 h-3" />
                              <span>{row.highlightLabel}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Recommendation */}
          {products.length >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 bg-gradient-to-r from-teal-500/10 to-indigo-500/10 border border-teal-500/30 rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                <span className="text-2xl">💡</span>
                <span>{t('compare.recommendation')}</span>
              </h3>
              <p className="text-slate-300">
                Based on value for money, <span className="text-teal-400 font-semibold">
                  {products.find(p => p.id === bestPricePerGB)?.name || products[0]?.name}
                </span> offers the best price per GB at €{getPricePerGB(products.find(p => p.id === bestPricePerGB) || products[0])}/GB.
                {products.find(p => p.id === mostData) && products.find(p => p.id === mostData)?.id !== bestPricePerGB && (
                  <> For maximum data, consider <span className="text-teal-400 font-semibold">
                    {products.find(p => p.id === mostData)?.name}
                  </span> with {getMetaValue(products.find(p => p.id === mostData), 'data')}.</>
                )}
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export function useCompare() {
  const [compareProducts, setCompareProducts] = useState([]);
  const MAX_COMPARE = 4;

  const addToCompare = (product) => {
    if (compareProducts.length >= MAX_COMPARE) {
      return false;
    }
    if (!compareProducts.find(p => p.id === product.id)) {
      setCompareProducts(prev => [...prev, product]);
      return true;
    }
    return false;
  };

  const removeFromCompare = (productId) => {
    setCompareProducts(prev => prev.filter(p => p.id !== productId));
  };

  const toggleCompare = (product) => {
    if (compareProducts.find(p => p.id === product.id)) {
      removeFromCompare(product.id);
    } else {
      addToCompare(product);
    }
  };

  const clearCompare = () => {
    setCompareProducts([]);
  };

  const isInCompare = (productId) => {
    return compareProducts.some(p => p.id === productId);
  };

  return {
    compareProducts,
    addToCompare,
    removeFromCompare,
    toggleCompare,
    clearCompare,
    isInCompare,
    maxCompare: MAX_COMPARE
  };
}
